import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { insertLicenseSchema, insertApplicationSchema } from "@shared/schema";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon, PlusCircle, X, Users, Building2, AlertTriangle, ChevronsUpDown, Check, UserCheck } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Application, Employee, Team } from "@shared/schema";
import { useSubscription } from "@/hooks/use-subscription";
import { UpgradePrompt } from "@/components/subscription/upgrade-prompt";
import { UpgradeCTAModal } from "@/components/subscription/upgrade-cta-modal";
import { useCurrency } from "@/hooks/use-currency";
import { useAppSettings } from "@/hooks/use-app-settings";
import { useAuth } from "@/hooks/use-auth";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ViewLicenseDialog from "./view-license-dialog";

type AddLicenseDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const STATIC_BILLING_CYCLES = [
  "Monthly",
  "Quarterly",
  "Semi-Annual",
  "Annual",
  "Biennial",
  "One-Time",
];

export default function SimpleAddLicenseDialog({ open, onOpenChange }: AddLicenseDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { canAddLicense, isAtLicenseLimit } = useSubscription();
  const { data: applications = [], isLoading: appsLoading } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });
  
  // Fetch employees for assignment
  const { data: employees = [], isLoading: employeesLoading } = useQuery<Employee[]>({
    queryKey: ['/api/employees'],
    enabled: open, // Only fetch when dialog is open
  });
  
  // Fetch teams for assignment
  const { data: teams = [], isLoading: teamsLoading } = useQuery<Team[]>({
    queryKey: ['/api/teams'],
    enabled: open, // Only fetch when dialog is open
  });
  
  const { activeCurrencies, baseCurrency } = useCurrency();
  const { getSettingsByType } = useAppSettings();

  // Application schema matching the main application form (excludes tenantId for form validation)
  const clientApplicationSchema = insertApplicationSchema.omit({ tenantId: true });
  
  const [showNewAppForm, setShowNewAppForm] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>("general");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [limitError, setLimitError] = useState<any>(null);
  const [customCategory, setCustomCategory] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  
  // Auto-expand create application form when no applications exist
  useEffect(() => {
    if (applications && applications.length === 0 && !showNewAppForm) {
      setShowNewAppForm(true);
    }
  }, [applications, showNewAppForm]);
  
  // State for tracking selected employees and teams
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<number[]>([]);
  
  // Form for creating a new application
  const newAppForm = useForm({
    resolver: zodResolver(clientApplicationSchema),
    defaultValues: {
      name: "",
      description: "",
      category: ""
    }
  });
  
  // Helper function to calculate renewal date based on billing cycle
  const calculateRenewalDate = (startDate: Date, billingCycle: string, bufferDays: number = 0): Date => {
    const renewalDate = new Date(startDate);
    
    switch (billingCycle.toLowerCase()) {
      case "monthly":
        renewalDate.setMonth(renewalDate.getMonth() + 1);
        break;
      case "quarterly":
        renewalDate.setMonth(renewalDate.getMonth() + 3);
        break;
      case "semi-annual":
      case "half-yearly":
      case "half yearly":
        renewalDate.setMonth(renewalDate.getMonth() + 6);
        break;
      case "yearly":
      case "annual":
        renewalDate.setFullYear(startDate.getFullYear() + 1);
        break;
      case "biennial":
        renewalDate.setFullYear(startDate.getFullYear() + 2);
        break;
      case "one-time":
      case "lifetime":
        renewalDate.setFullYear(startDate.getFullYear() + 50);
        break;
      default:
        renewalDate.setMonth(renewalDate.getMonth() + 1);
    }
    
    // Add buffer days if specified
    if (bufferDays !== 0) {
      renewalDate.setDate(renewalDate.getDate() + bufferDays);
    }
    
    return renewalDate;
  };
  
  // Helper function to validate renewal date against billing cycle
  const validateRenewalDate = (startDate: Date, renewalDate: Date, billingCycle: string): { isValid: boolean; warning?: string; error?: string } => {
    if (!startDate || !renewalDate || !billingCycle) {
      return { isValid: true };
    }
    
    const expectedRenewalDate = calculateRenewalDate(startDate, billingCycle);
    
    // Calculate the difference in days
    const diffTime = renewalDate.getTime() - expectedRenewalDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    // Allow 5 days buffer by default
    const bufferDays = 5;
    
    if (Math.abs(diffDays) > bufferDays) {
      return {
        isValid: false,
        error: `Renewal date is ${Math.abs(diffDays)} days ${diffDays > 0 ? 'after' : 'before'} the expected ${billingCycle} cycle. Expected around ${expectedRenewalDate.toISOString().split('T')[0]} (±${bufferDays} days buffer).`
      };
    } else if (Math.abs(diffDays) > 0) {
      return {
        isValid: true,
        warning: `Renewal date is ${Math.abs(diffDays)} days ${diffDays > 0 ? 'after' : 'before'} the expected ${billingCycle} cycle (within buffer range).`
      };
    }
    
    return { isValid: true };
  };
  
  // State for date validation
  const [dateValidation, setDateValidation] = useState<{ isValid: boolean; warning?: string; error?: string }>({ isValid: true });

  // Explicitly controlled so each calendar closes the instant a date is picked --
  // Radix Popovers don't auto-close on their own content being interacted with,
  // so without this the popup stayed open showing the old value underneath,
  // making a successful selection look like it hadn't registered at all.
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [renewalDateOpen, setRenewalDateOpen] = useState(false);

  // Set right after creating a backdated (Expired) license, so the user flows
  // straight into recording its renewal history instead of a separate trip.
  const [historicalPromptLicenseId, setHistoricalPromptLicenseId] = useState<number | null>(null);

  // Product owner state
  const [productOwnerId, setProductOwnerId] = useState<number | null>(null);
  const [ownerComboOpen, setOwnerComboOpen] = useState(false);

  const getDefaultLicenseValues = () => ({
    applicationId: 0,
    name: "",
    totalSeats: 1,
    assignedSeats: 0,
    cost: 0,
    currency: "USD",
    billingCycle: "Monthly",
    startDate: new Date(),
    renewalDate: calculateRenewalDate(new Date(), "Monthly"),
    status: "active"
  });

  // Initialize form
  const form = useForm({
    resolver: zodResolver(insertLicenseSchema),
    defaultValues: getDefaultLicenseValues()
  });

  const resetDialogState = () => {
    form.reset(getDefaultLicenseValues());
    newAppForm.reset({
      name: "",
      description: "",
      category: ""
    });
    setSelectedEmployees([]);
    setSelectedTeams([]);
    setProductOwnerId(null);
    setOwnerComboOpen(false);
    setShowNewAppForm(applications.length === 0);
    setSelectedTab("general");
    setCustomCategory("");
    setSelectedCategory("");
    setDateValidation({ isValid: true });
    setLimitError(null);
    setStartDateOpen(false);
    setRenewalDateOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetDialogState();
    }

    onOpenChange(nextOpen);
  };

  
  // Effect to update assignedSeats count based on selected employees and teams
  useEffect(() => {
    // For now, we'll count each employee as 1 seat
    // In the future, we could enhance this to count team members properly
    const totalAssigned = selectedEmployees.length + selectedTeams.length;
    form.setValue("assignedSeats", totalAssigned);
  }, [selectedEmployees, selectedTeams, form]);
  
  // Mirrors the server's auto-status logic (routes.ts POST /api/licenses) so a
  // past renewal date (e.g. backfilling historical license data) is always
  // paired with status "expired" instead of the stale "active" default --
  // that mismatch is exactly what silently failed the shared Zod schema's
  // status/renewalDate refine with no visible error.
  const calculateLicenseStatus = (renewalDate: Date, billingCycle: string): string => {
    if (["Lifetime", "One-Time"].includes(billingCycle)) return "active";
    return new Date(renewalDate) < new Date() ? "expired" : "active";
  };

  // Effect to calculate renewal date when start date or billing cycle changes.
  // Each branch only calls setValue when the computed value actually differs
  // from the current one -- form.watch() re-fires on every setValue (even to
  // an "unchanged" value, since calculateRenewalDate returns a fresh Date
  // object each call), so without these guards a reset (e.g. closing the
  // dialog, which calls form.reset()) touches startDate/billingCycle/
  // renewalDate/status together and the two branches below keep re-triggering
  // each other -- an infinite loop that overflows the call stack and leaves
  // the dialog's close handler never completing.
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if ((name === "startDate" || name === "billingCycle") && value.startDate && value.billingCycle) {
        const renewalDate = calculateRenewalDate(new Date(value.startDate), value.billingCycle);
        if (!value.renewalDate || new Date(value.renewalDate).getTime() !== renewalDate.getTime()) {
          form.setValue("renewalDate", renewalDate);
        }
        const newStatus = calculateLicenseStatus(renewalDate, value.billingCycle);
        if (value.status !== newStatus) {
          form.setValue("status", newStatus, { shouldValidate: true });
        }

        // Reset validation when auto-calculating
        setDateValidation({ isValid: true });
      }

      // Validate renewal date when it changes manually
      if (name === "renewalDate" && value.startDate && value.renewalDate && value.billingCycle) {
        const validation = validateRenewalDate(new Date(value.startDate), new Date(value.renewalDate), value.billingCycle);
        setDateValidation(validation);
        const newStatus = calculateLicenseStatus(new Date(value.renewalDate), value.billingCycle);
        if (value.status !== newStatus) {
          form.setValue("status", newStatus, { shouldValidate: true });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Clear applicationId validation error when showing new app form
  useEffect(() => {
    if (showNewAppForm) {
      form.clearErrors("applicationId");
    }
  }, [showNewAppForm, form]);
  
  // Mutation for creating a new category
  const createCategoryMutation = useMutation({
    mutationFn: async (categoryName: string) => {
      return apiRequest("/api/app-settings", {
        method: "POST",
        body: {
          type: "category",
          value: categoryName,
          isActive: true
        }
      });
    },
  });

  // Mutation for creating a new application
  const createAppMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log("DEBUG: Simple dialog - Application mutation starting with data:", data);
      console.log("DEBUG: Simple dialog - Data keys:", Object.keys(data || {}));
      console.log("DEBUG: Simple dialog - Data values:", Object.values(data || {}));
      
      if (!data || Object.keys(data).length === 0) {
        console.error("DEBUG: Simple dialog - Empty data object passed to application mutation");
        throw new Error("No application data provided");
      }

      // If using custom category, create it first
      if (selectedCategory === "Other" && customCategory.trim()) {
        await createCategoryMutation.mutateAsync(customCategory.trim());
        data.category = customCategory.trim();
      }
      
      return apiRequest("/api/applications", { method: "POST", body: data });
    },
    onSuccess: (newApp) => {
      queryClient.invalidateQueries({ queryKey: ['/api/applications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/subscription-status'] });
      toast({
        title: "Application created",
        description: "The application has been created successfully."
      });
      // Update the license form with the new application ID
      form.setValue("applicationId", newApp.id);
      // Hide the new application form
      setShowNewAppForm(false);
      // Reset the application form and state
      newAppForm.reset();
      setSelectedCategory("");
      setCustomCategory("");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to create application: ${error.message}`,
        variant: "destructive"
      });
    }
  });
  
  // Mutation for creating a new license
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("/api/licenses", { method: "POST", body: data });
    },
    onSuccess: (newLicense) => {
      // Process employee assignments sequentially to avoid race conditions
      const processEmployeeAssignments = async () => {
        if (selectedEmployees.length > 0) {
          for (const employeeId of selectedEmployees) {
            try {
              await assignEmployeeMutation.mutateAsync({
                licenseId: newLicense.id,
                employeeId: Number(employeeId),
                assignedBy: 1,  // Assuming current user ID or fetch from context
                status: 'active'
              });
            } catch (error) {
              console.error("Error assigning employee:", error);
              // Continue with other assignments even if one fails
            }
          }
        }
      };
      
      // Process team assignments sequentially to avoid race conditions
      const processTeamAssignments = async () => {
        if (selectedTeams.length > 0) {
          for (const teamId of selectedTeams) {
            try {
              await assignTeamMutation.mutateAsync({
                licenseId: newLicense.id,
                teamId: Number(teamId),
                assignedBy: 1,  // Assuming current user ID or fetch from context
                status: 'active'
              });
            } catch (error) {
              console.error("Error assigning team:", error);
              // Continue with other assignments even if one fails
            }
          }
        }
      };
      
      // Run assignments and then update UI
      Promise.all([
        processEmployeeAssignments(),
        processTeamAssignments()
      ]).finally(() => {
        queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
        queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
        queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
        toast({
          title: "License added",
          description: "The license has been added successfully.",
        });
        resetDialogState();
        onOpenChange(false);
        // Backdated (Expired) license -- flow straight into recording its
        // renewal history instead of leaving that as a separate trip.
        if (newLicense.status === 'expired') {
          setHistoricalPromptLicenseId(newLicense.id);
        }
      });
    },
    onError: (error: any) => {
      console.log("🔍 SIMPLE DIALOG License creation error:", error);
      console.log("🔍 SIMPLE DIALOG Error data:", error.data);
      
      // Check if this is a license limit error from backend response
      if ((error.message === "LICENSE_LIMIT_REACHED" && error.upgradeData) || 
          (error.data?.message === "LICENSE_LIMIT_REACHED" && error.data?.upgradeData)) {
        console.log("🔄 SIMPLE DIALOG License limit reached, showing upgrade modal (no license created)");
        const upgradeData = error.upgradeData || error.data.upgradeData;
        setLimitError(upgradeData);
        setUpgradeModalOpen(true);
        return;
      }
      
      // For other errors, show regular error toast
      toast({
        title: "Error",
        description: error.message || "Failed to create license",
        variant: "destructive"
      });
    }
  });
  
  // Mutation for assigning an employee to a license
  const assignEmployeeMutation = useMutation({
    mutationFn: async (data: any) => {
      // Ensure employeeId is a number
      const payload = {
        ...data,
        employeeId: Number(data.employeeId),
        licenseId: Number(data.licenseId)
      };
      
      return apiRequest("/api/license-employees", { 
        method: "POST", 
        body: payload 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
    },
    onError: (error) => {
      console.error("Error assigning employee:", error);
      // We don't show this toast since we're handling multiple assignments
      // and don't want to flood the user with error messages
    }
  });
  
  // Mutation for assigning a team to a license
  const assignTeamMutation = useMutation({
    mutationFn: async (data: any) => {
      // Ensure teamId is a number
      const payload = {
        ...data,
        teamId: Number(data.teamId),
        licenseId: Number(data.licenseId)
      };
      
      return apiRequest("/api/license-teams", {
        method: "POST",
        body: payload
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
    },
    onError: (error) => {
      console.error("Error assigning team:", error);
      // We don't show this toast since we're handling multiple assignments
      // and don't want to flood the user with error messages
    }
  });

  async function onSubmit(data: any) {
    try {
      console.log("🚀 DEBUG: onSubmit function CALLED!");
      console.log("DEBUG: Simple dialog onSubmit called with data:", data);
      console.log("DEBUG: Simple dialog showNewAppForm:", showNewAppForm);
      
      // FIRST: Check license limits before doing anything
      console.log("🔍 Checking license limits before creation...");
      
      try {
        const limitsResponse = await apiRequest("/api/subscription/plan-limits", {
          method: "GET"
        });
        
        console.log("📊 Current limits:", limitsResponse);
        
        if (limitsResponse.usage && limitsResponse.limits) {
          const currentLicenses = limitsResponse.usage.licenses || 0;
          const licenseLimit = limitsResponse.limits.licenses || 0;
          
          console.log(`📝 License check: ${currentLicenses}/${licenseLimit}`);
          
          if (currentLicenses >= licenseLimit) {
            console.log("🚫 License limit reached, showing upgrade modal");
            // Show upgrade modal instead of error toast
            setLimitError({
              limitType: 'license_limit',
              currentPlan: 'Single',
              currentUsage: currentLicenses,
              limit: licenseLimit
            });
            setUpgradeModalOpen(true);
            return; // Stop here - don't create anything
          }
        }
      } catch (error: any) {
        console.warn("Failed to check limits, proceeding with creation:", error);
      }
      
      // If showing new app form, we must create the application first
      if (showNewAppForm) {
        const appData = newAppForm.getValues();
        console.log("DEBUG: Simple dialog - newAppForm.getValues() returned:", appData);
        
        // Validate the new application form first (without tenantId)
        const isAppFormValid = await newAppForm.trigger();
        console.log("DEBUG: Simple dialog - isAppFormValid:", isAppFormValid);
        
        if (!isAppFormValid) {
          console.log("DEBUG: Simple dialog - Form validation failed");
          const formErrors = newAppForm.formState.errors;
          console.log("DEBUG: Simple dialog - Form errors:", formErrors);
          
          // Show specific error messages for each field
          const errorFields = Object.keys(formErrors);
          let errorMessage = "Please fix the following errors:\n";
          errorFields.forEach(field => {
            const error = formErrors[field as keyof typeof formErrors];
            if (error?.message) {
              errorMessage += `• ${error.message}\n`;
            }
          });
          
          toast({
            title: "Application Required",
            description: errorMessage.trim() || "Please complete all required fields for the new application.",
            variant: "destructive"
          });
          return;
        }
        
        // Add tenantId to the application data after form validation passes
        const appDataWithTenant = {
          ...appData,
          tenantId: user?.tenantId || 0
        };
        console.log("DEBUG: Simple dialog - appData with tenantId:", appDataWithTenant);
        
        // Create the application
        try {
          console.log("DEBUG: Simple dialog - About to call createAppMutation with:", appDataWithTenant);
          const newApp = await createAppMutation.mutateAsync(appDataWithTenant);
          console.log("DEBUG: Simple dialog - createAppMutation succeeded:", newApp);
          data.applicationId = newApp.id; // Use the new application ID
        } catch (error) {
          console.error("DEBUG: Simple dialog - createAppMutation failed:", error);
          // Error is already handled in the mutation
          return;
        }
      } else {
        // If not showing new app form, validate that we have an application ID
        if (!data.applicationId || data.applicationId === 0) {
          toast({
            title: "Application Required",
            description: "Please select an application.",
            variant: "destructive"
          });
          return;
        }
      }
      
      // Ensure values are the correct types
      const formattedData = {
        ...data,
        applicationId: typeof data.applicationId === 'string' ? parseInt(data.applicationId) : data.applicationId,
        totalSeats: typeof data.totalSeats === 'string' ? parseInt(data.totalSeats) : data.totalSeats,
        assignedSeats: typeof data.assignedSeats === 'string' ? parseInt(data.assignedSeats) : data.assignedSeats,
        cost: typeof data.cost === 'string' ? parseFloat(data.cost) : data.cost,
        productOwnerId: productOwnerId ?? null,
      };
      
      // Create the license
      mutation.mutate(formattedData);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create license",
        variant: "destructive"
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>Add New License</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 space-y-4 py-2">
            <FormField
              control={form.control}
              name="applicationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Application*</FormLabel>
                  {showNewAppForm && (
                    <p className="text-xs text-muted-foreground mb-2">
                      Creating a new application below. The application will be created when you submit this form.
                    </p>
                  )}
                  <div className="space-y-2">
                    {applications && applications.length > 0 ? (
                      <>
                        <Select 
                          disabled={appsLoading || showNewAppForm}
                          onValueChange={(value) => {
                            if (value === "create-new") {
                              setShowNewAppForm(true);
                              field.onChange(0); // Reset to 0 or empty state
                            } else {
                              field.onChange(parseInt(value));
                              setShowNewAppForm(false);
                            }
                          }} 
                          value={field.value !== undefined && field.value !== 0 ? field.value.toString() : ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select application" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="create-new">
                              <div className="flex items-center text-primary">
                                <PlusCircle className="mr-2 h-4 w-4" />
                                Create New Application
                              </div>
                            </SelectItem>
                            <Separator className="my-2" />
                            {applications?.map((app: Application) => (
                              <SelectItem key={app.id} value={app.id.toString()}>
                                {app.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {showNewAppForm && (
                          <p className="text-xs text-muted-foreground">
                            Creating a new application. Select an existing application above to cancel.
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="p-3 border rounded-md bg-muted/30 border-dashed">
                        <div className="flex items-center justify-center text-muted-foreground">
                          <Building2 className="mr-2 h-5 w-5" />
                          <span className="text-sm">No applications found. Create your first application below.</span>
                        </div>
                      </div>
                    )}

                    {showNewAppForm && (
                      <div className="p-4 border rounded-md mt-2 bg-muted/30 border-primary/20">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-medium flex items-center text-primary">
                            <PlusCircle className="mr-2 h-4 w-4" />
                            Create New Application
                          </h4>
                          {applications && applications.length > 0 && (
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              className="h-8 px-2 text-muted-foreground"
                              onClick={() => setShowNewAppForm(false)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <Form {...newAppForm}>
                          <div className="space-y-3">
                            <FormField
                              control={newAppForm.control}
                              name="name"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Application Name*</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., Microsoft 365, Slack, ChatGPT" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={newAppForm.control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Description</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Brief description of the application" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={newAppForm.control}
                              name="category"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Category*</FormLabel>
                                  <Select 
                                    onValueChange={(value) => {
                                      setSelectedCategory(value);
                                      if (value !== "Other") {
                                        field.onChange(value);
                                      }
                                    }} 
                                    value={selectedCategory || field.value}
                                  >
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {getSettingsByType("category").filter(setting => setting.isActive && setting.value.toLowerCase() !== "other").map((setting) => (
                                        <SelectItem key={setting.id} value={setting.value}>
                                          {setting.value}
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                  {selectedCategory === "Other" && (
                                    <div className="mt-2">
                                      <Input
                                        placeholder="Enter custom category"
                                        value={customCategory}
                                        onChange={(e) => {
                                          setCustomCategory(e.target.value);
                                          field.onChange(e.target.value);
                                        }}
                                      />
                                    </div>
                                  )}
                                </FormItem>
                              )}
                            />
                          </div>
                        </Form>
                      </div>
                    )}
                  </div>
                  {!showNewAppForm && <FormMessage />}
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>License Name*</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Enterprise Plan" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="totalSeats"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Seats*</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      value={field.value}
                      onChange={(e) => {
                        const value = e.target.value === "" ? "" : Number(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
              
            <div className="pt-2">
              <Tabs defaultValue="general" onValueChange={setSelectedTab} value={selectedTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="employees" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Employees
                  </TabsTrigger>
                  <TabsTrigger value="teams" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Teams
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="general">
                  {/* General information content is shown on the primary form */}
                </TabsContent>
                
                <TabsContent value="employees" className="border rounded-md p-4 mt-2">
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium">Assign to Employees</h3>
                    <p className="text-sm text-muted-foreground">
                      Select employees who should be assigned to this license
                    </p>
                    
                    {employeesLoading ? (
                      <div>Loading employees...</div>
                    ) : employees.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        <p>No employees found. You'll need to add employees first.</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto p-2 border rounded-md">
                        {employees.map((employee) => (
                          <div key={employee.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`employee-${employee.id}`}
                              className="rounded border-gray-300 text-primary focus:ring-primary"
                              checked={selectedEmployees.includes(employee.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEmployees([...selectedEmployees, employee.id]);
                                } else {
                                  setSelectedEmployees(selectedEmployees.filter(id => id !== employee.id));
                                }
                              }}
                            />
                            <label htmlFor={`employee-${employee.id}`} className="text-sm">
                              {employee.fullName}
                              {employee.jobTitle && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  {employee.jobTitle}
                                </span>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* For now we'll set assignedSeats based on the number of selected employees */}
                    <input 
                      type="hidden" 
                      value={selectedEmployees.length} 
                      onChange={(e) => {
                        form.setValue("assignedSeats", selectedEmployees.length);
                      }}
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="teams" className="border rounded-md p-4 mt-2">
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium">Assign to Teams</h3>
                    <p className="text-sm text-muted-foreground">
                      Select teams that should be assigned to this license
                    </p>
                    
                    {teamsLoading ? (
                      <div>Loading teams...</div>
                    ) : teams.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        <p>No teams found. You'll need to add teams first.</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto p-2 border rounded-md">
                        {teams.map((team) => (
                          <div key={team.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`team-${team.id}`}
                              className="rounded border-gray-300 text-primary focus:ring-primary"
                              checked={selectedTeams.includes(team.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTeams([...selectedTeams, team.id]);
                                } else {
                                  setSelectedTeams(selectedTeams.filter(id => id !== team.id));
                                }
                              }}
                            />
                            <label htmlFor={`team-${team.id}`} className="text-sm">
                              {team.name}
                              {/* Department field has been removed from schema */}
                              <span className="text-xs text-muted-foreground ml-2">
                                {team.manager ? `Manager: ${team.manager}` : ''}
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Total Subscription Cost*</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                        <Input 
                          type="text" 
                          className="pl-8"
                          placeholder="0.00"
                          value={field.value}
                          onChange={(e) => {
                            // Only allow numbers and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const formatted = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            field.onChange(parseFloat(formatted) || 0);
                            e.target.value = formatted;
                          }}
                        />
                      </div>
                    </FormControl>
                    <p className="text-xs text-muted-foreground mt-1">Enter the total cost for the entire subscription, not per seat</p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="billingCycle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Cycle<span className="text-red-500 ml-1">*</span></FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);

                        const startDate = form.getValues("startDate");
                        if (startDate) {
                          const renewalDate = calculateRenewalDate(startDate, value);
                          form.setValue("renewalDate", renewalDate);
                          setDateValidation({ isValid: true });
                        }
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select billing cycle" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {STATIC_BILLING_CYCLES.map((cycle) => (
                          <SelectItem key={cycle} value={cycle}>
                            {cycle}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start Date*</FormLabel>
                    <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={"pl-3 text-left font-normal"}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          defaultMonth={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setStartDateOpen(false);

                            // When start date changes, update renewal date based on billing cycle
                            if (date) {
                              const billingCycle = form.getValues("billingCycle");
                              if (billingCycle) {
                                const renewalDate = calculateRenewalDate(date, billingCycle);
                                form.setValue("renewalDate", renewalDate);

                                // Reset validation when auto-calculating
                                setDateValidation({ isValid: true });
                              }
                            }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="renewalDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Renewal Date*</FormLabel>
                    <Popover open={renewalDateOpen} onOpenChange={setRenewalDateOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={`pl-3 text-left font-normal ${
                              dateValidation.error ? 'date-error' :
                              dateValidation.warning ? 'date-warning' : ''
                            }`}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          defaultMonth={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setRenewalDateOpen(false);
                            // Validate when date changes manually
                            const startDate = form.getValues("startDate");
                            const billingCycle = form.getValues("billingCycle");
                            if (startDate && date && billingCycle) {
                              const validation = validateRenewalDate(startDate, date, billingCycle);
                              setDateValidation(validation);
                            }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {dateValidation.warning && (
                      <p className="text-amber-600 text-sm flex items-center mt-1">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        {dateValidation.warning}
                      </p>
                    )}
                    {dateValidation.error && (
                      <p className="text-red-600 text-sm flex items-center mt-1">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        {dateValidation.error}
                      </p>
                    )}
                    {form.watch("status") === "expired" && (
                      <p className="text-slate-500 text-sm flex items-center mt-1">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        This renewal date has already passed, so this license will be added as Expired — useful for backfilling historical records.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            
            {/* Product Owner */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Product Owner <span className="text-muted-foreground text-xs">(optional)</span></label>
              <Popover open={ownerComboOpen} onOpenChange={setOwnerComboOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                    {productOwnerId
                      ? (employees as Employee[]).find((e) => e.id === productOwnerId)?.fullName ?? "Select owner"
                      : "Select owner"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search people..." />
                    <CommandList>
                      <CommandEmpty>No people found.</CommandEmpty>
                      <CommandGroup>
                        {productOwnerId && (
                          <CommandItem value="__clear__" onSelect={() => { setProductOwnerId(null); setOwnerComboOpen(false); }}>
                            <X className="mr-2 h-4 w-4 text-muted-foreground" />
                            Clear selection
                          </CommandItem>
                        )}
                        {(employees as Employee[]).map((emp) => (
                          <CommandItem
                            key={emp.id}
                            value={emp.fullName}
                            onSelect={() => { setProductOwnerId(emp.id); setOwnerComboOpen(false); }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", productOwnerId === emp.id ? "opacity-100" : "opacity-0")} />
                            <div className="flex flex-col">
                              <span className="font-medium">{emp.fullName}</span>
                              {emp.jobTitle && <span className="text-xs text-muted-foreground">{emp.jobTitle}</span>}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

            <DialogFooter className="px-6 py-4 border-t shrink-0">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={mutation.isPending}
                onClick={async (e) => {
                  console.log("DEBUG: Add License button clicked");
                  console.log("DEBUG: Form state errors:", form.formState.errors);
                  console.log("DEBUG: Form values:", form.getValues());
                  console.log("DEBUG: showNewAppForm:", showNewAppForm);
                  console.log("DEBUG: Form isValid:", form.formState.isValid);
                  console.log("DEBUG: Form isSubmitting:", form.formState.isSubmitting);
                  
                  // If we're showing new app form and form is invalid due to applicationId,
                  // bypass the default form submission and call onSubmit directly
                  if (showNewAppForm && !form.formState.isValid) {
                    e.preventDefault();
                    console.log("🔄 DEBUG: Bypassing form validation for new app creation");
                    await onSubmit(form.getValues());
                  }
                }}
              >
                {mutation.isPending ? "Adding..." : "Add License"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
      
      {/* Upgrade CTA Modal */}
      <UpgradeCTAModal
        isOpen={upgradeModalOpen}
        onClose={() => {
          setUpgradeModalOpen(false);
          setLimitError(null);
        }}
        limitType={limitError?.limitType || 'license_limit'}
        currentPlan={limitError?.currentPlan || 'Single'}
        currentUsage={limitError?.currentUsage || 0}
        limit={limitError?.limit || 0}
      />

      {historicalPromptLicenseId !== null && (
        <ViewLicenseDialog
          licenseId={historicalPromptLicenseId}
          isOpen={true}
          onClose={() => setHistoricalPromptLicenseId(null)}
          autoOpenHistoricalRenewal
        />
      )}
    </Dialog>
  );
}
